import { useState } from "react";
import { T } from "@/lib/theme";
import { ArrowUpRight } from "lucide-react";
import EMark from "@/components/EMark";

const ENQUIRY_TYPES = [
  "I want to launch a business",
  "I need practical landing support",
  "I want to explore institutional systems",
  "I want to discuss trade or market access",
  "I want to discuss digital interoperability",
  "I want to start a strategic conversation",
];

/**
 * Contact — start a serious conversation. Form is client-side only;
 * directs the message through the most relevant orbit line on receipt.
 */
export default function Contact() {
  const [state, setState] = useState({
    name: "",
    org: "",
    email: "",
    enquiry: ENQUIRY_TYPES[0],
    message: "",
  });
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!state.name || !state.email || !state.message) return;

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: state.name,
          org: state.org,
          email: state.email,
          enquiry: state.enquiry,
          message: state.message,
        }),
      });

      if (!response.ok) {
        throw new Error("Request failed");
      }

      setSent(true);
    } catch (error) {
      window.alert("Your message could not be sent. Please try again or email info@erenown.com.");
    }
  };

  return (
    <div data-testid="contact-page">
      {/* HERO */}
      <section className="relative overflow-hidden" style={{ background: T.bg }}>
        <div aria-hidden className="absolute inset-0 opacity-30 pointer-events-none" style={{
          backgroundImage: `radial-gradient(circle at center, ${T.border}66 1px, transparent 1px)`,
          backgroundSize: "48px 48px",
          maskImage: "radial-gradient(ellipse 70% 60% at 50% 30%, black, transparent 82%)",
        }} />
        <div className="relative max-w-[1200px] mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-20">
          <div className="flex items-center gap-4 font-plexmono text-[10.5px] tracking-[0.22em] uppercase" style={{ color: T.muted }}>
            <span className="inline-flex items-center gap-2"><EMark /> · CONTACT · MMXX</span>
            <span className="flex-1 h-px" style={{ background: T.border }} />
            <span style={{ color: T.accent }}>Reviewed with discretion</span>
          </div>
          <span className="block mt-10 font-plexmono text-[10.5px] tracking-[0.28em] uppercase" style={{ color: T.accent }}>
            CONTACT
          </span>
          <h1 className="font-fraunces font-light text-5xl sm:text-6xl lg:text-[72px] leading-[1.02] tracking-[-0.015em] mt-6 max-w-3xl" style={{ color: T.text, fontVariationSettings: "'SOFT' 50" }}>
            Begin a <span className="italic" style={{ color: T.accent }}>strategic</span> conversation.
          </h1>
          <p className="mt-8 max-w-2xl font-intertight text-[16px] leading-[1.9]" style={{ color: T.textSoft }}>
            Whether you are exploring a new market, considering a structured launch, evaluating institutional
            needs, or looking for a more connected operating model, Erenown offers a focused point of entry.
          </p>
        </div>
      </section>

      {/* FORM SECTION — How we can begin */}
      <section className="border-t" style={{ borderColor: T.border, background: T.surface }} data-testid="contact-form-section">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 py-16 md:py-24 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-5">
            <div className="font-plexmono text-[10.5px] tracking-[0.24em] uppercase mb-4" style={{ color: T.accent }}>
              How we can begin
            </div>
            <h2 className="font-fraunces font-normal text-3xl md:text-4xl leading-[1.2]" style={{ color: T.text, fontVariationSettings: "'SOFT' 50" }}>
              Clear, not complex.
            </h2>
            <p className="mt-6 max-w-md font-intertight text-[15px] leading-[1.85]" style={{ color: T.textSoft }}>
              The first step does not need to be complex. It simply needs to be clear. Use the form to outline
              your interest, and Erenown will direct the conversation through the most relevant line within its
              orbit.
            </p>

            <div className="mt-10">
              <div className="font-plexmono text-[10px] tracking-[0.28em] uppercase mb-3" style={{ color: T.muted }}>
                Primary contact
              </div>
              <a href="mailto:info@erenown.com" data-testid="contact-email"
                className="inline-block font-fraunces text-2xl md:text-3xl border-b" style={{ color: T.text, borderColor: T.accent }}>
                info@erenown.com
              </a>
            </div>

            <div className="mt-10" data-testid="contact-entities">
              <div className="font-plexmono text-[10px] tracking-[0.28em] uppercase mb-4" style={{ color: T.muted }}>
                Erenown Entities
              </div>

              <div className="mb-5">
                <div className="font-plexmono text-[10px] tracking-[0.28em] uppercase mb-1.5" style={{ color: T.mutedSoft }}>
                  United Kingdom
                </div>
                <div className="font-fraunces text-[17px] leading-[1.4]" style={{ color: T.text }}>
                  Erenown Ltd
                </div>
                <div className="mt-1 font-intertight text-[13px] leading-[1.75]" style={{ color: T.mutedSoft }}>
                  Registered in England and Wales · Company No. 12845592<br />
                  83 St Marks Road, Enfield, EN1 1BJ, United Kingdom
                </div>
              </div>

              <div>
                <div className="font-plexmono text-[10px] tracking-[0.28em] uppercase mb-1.5" style={{ color: T.mutedSoft }}>
                  Kosovo
                </div>
                <div className="font-fraunces text-[17px] leading-[1.4]" style={{ color: T.text }}>
                  Erenown LLC
                </div>
                <div className="mt-1 font-intertight text-[13px] leading-[1.75]" style={{ color: T.mutedSoft }}>
                  Ulpianë, C2, H-III, Nr.6, Bulevardi “Dëshmorët e Kombit”,<br />
                  Prishtinë 10000, Republic of Kosovo
                </div>
              </div>

              <p className="mt-5 max-w-sm font-intertight text-[12.5px] leading-[1.85]" style={{ color: T.mutedSoft }}>
                Operating through independently established Erenown entities in the United Kingdom and Kosovo.
              </p>
            </div>

            <div className="mt-10 max-w-sm font-intertight text-[13.5px] leading-[1.85]" style={{ color: T.mutedSoft }}>
              All enquiries are reviewed with discretion and directed through the most relevant structure
              within the Erenown orbit.
            </div>
          </div>

          <div className="col-span-12 lg:col-span-7">
            {sent ? (
              <div data-testid="contact-form-success" className="border p-10 md:p-12" style={{ borderColor: T.accent, background: T.surface2 }}>
                <div className="font-plexmono text-[10.5px] tracking-[0.28em] uppercase" style={{ color: T.accent }}>
                  Received · Thank you
                </div>
                <h3 className="font-fraunces font-normal text-3xl md:text-4xl mt-5" style={{ color: T.text }}>
                  Begin at the center.
                </h3>
                <p className="mt-5 font-intertight text-[15px] leading-[1.85]" style={{ color: T.textSoft }}>
                  Your note has been received and will be directed through the most relevant line within the
                  Erenown orbit. It will not be shared beyond Erenown.
                </p>
                <button data-testid="contact-reset" onClick={() => { setSent(false); setState({ name: "", org: "", email: "", enquiry: ENQUIRY_TYPES[0], message: "" }); }}
                  className="mt-8 font-plexmono text-[11px] tracking-[0.24em] uppercase border-b pb-1" style={{ color: T.accent, borderColor: T.accent }}>
                  Write another
                </button>
              </div>
            ) : (
              <form data-testid="contact-form" onSubmit={submit} className="border p-8 md:p-10" style={{ borderColor: T.border, background: T.surface2 }}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field id="name" label="Name" value={state.name} required onChange={(v) => setState({ ...state, name: v })} />
                  <Field id="org" label="Organisation" value={state.org} onChange={(v) => setState({ ...state, org: v })} />
                  <Field id="email" label="Email" type="email" value={state.email} required onChange={(v) => setState({ ...state, email: v })} full />

                  {/* Enquiry type select */}
                  <label htmlFor="enquiry" className="md:col-span-2">
                    <span className="block font-plexmono text-[10px] tracking-[0.24em] uppercase mb-2" style={{ color: T.muted }}>
                      Enquiry type<span style={{ color: T.accent }}> *</span>
                    </span>
                    <select
                      id="enquiry"
                      data-testid="contact-field-enquiry"
                      value={state.enquiry}
                      onChange={(e) => setState({ ...state, enquiry: e.target.value })}
                      style={{
                        background: "transparent",
                        border: "none",
                        borderBottom: `1px solid ${T.border}`,
                        color: T.text,
                        padding: "10px 0",
                        outline: "none",
                        width: "100%",
                        fontFamily: "Inter Tight, sans-serif",
                        fontSize: 15,
                        appearance: "none",
                      }}
                      onFocus={(e) => (e.target.style.borderBottomColor = T.accent)}
                      onBlur={(e) => (e.target.style.borderBottomColor = T.border)}
                    >
                      {ENQUIRY_TYPES.map((type) => (
                        <option key={type} value={type} style={{ background: T.surface2, color: T.text }}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </label>

                  <Field id="message" label="Outline of enquiry" value={state.message} required textarea onChange={(v) => setState({ ...state, message: v })} full />
                </div>
                <div className="mt-8 flex items-center justify-between gap-6 flex-wrap">
                  <div className="font-plexmono text-[10.5px] tracking-[0.22em] uppercase" style={{ color: T.muted }}>
                    Begin at the center.
                  </div>
                  <button type="submit" data-testid="contact-submit"
                    className="group inline-flex items-center gap-3 px-7 py-4 font-plexmono text-[11px] tracking-[0.28em] uppercase" style={{ background: T.warm, color: T.bg }}>
                    Start a Conversation <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ id, label, value, onChange, type = "text", full, textarea, required }) {
  const commonStyle = {
    background: "transparent",
    border: "none",
    borderBottom: `1px solid ${T.border}`,
    color: T.text,
    padding: "10px 0",
    outline: "none",
    width: "100%",
    fontFamily: "Inter Tight, sans-serif",
    fontSize: 15,
  };
  return (
    <label htmlFor={id} className={full ? "md:col-span-2" : ""}>
      <span className="block font-plexmono text-[10px] tracking-[0.24em] uppercase mb-2" style={{ color: T.muted }}>
        {label}{required && <span style={{ color: T.accent }}> *</span>}
      </span>
      {textarea ? (
        <textarea
          id={id}
          data-testid={`contact-field-${id}`}
          value={value}
          required={required}
          onChange={(e) => onChange(e.target.value)}
          rows={5}
          style={commonStyle}
          onFocus={(e) => (e.target.style.borderBottomColor = T.accent)}
          onBlur={(e) => (e.target.style.borderBottomColor = T.border)}
        />
      ) : (
        <input
          id={id}
          data-testid={`contact-field-${id}`}
          type={type}
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={commonStyle}
          onFocus={(e) => (e.target.style.borderBottomColor = T.accent)}
          onBlur={(e) => (e.target.style.borderBottomColor = T.border)}
        />
      )}
    </label>
  );
}
